import { useState } from "react";
import { FiPlus, FiDownload, FiTrash2, FiUsers, FiTrendingUp, FiCheckCircle } from "react-icons/fi";
import {
  Button, Card, PageHeader, StatusBadge, StatTile, DataTable, EmptyState, ProgressBar,
  FormField, Tabs, Banner, Avatar, PersonCell, SearchInput, Modal, Loader,
} from "../../components/common";
import RatingScale from "../../components/performance/RatingScale";
import WeightageMeter from "../../components/goals/WeightageMeter";

const COLORS = [
  { group: "Brand", items: [["--pms-primary", "Primary actions, active nav"], ["--pms-primary-dark", "Hover / pressed"], ["--pms-primary-light", "Selected rows, active nav background"], ["--pms-primary-soft", "Row hover"]] },
  { group: "Surface", items: [["--pms-bg", "App background"], ["--pms-card-bg", "Cards, tables, modals"], ["--pms-border", "Card and table borders"], ["--pms-border-soft", "Dividers inside a card"]] },
  { group: "Text", items: [["--pms-text", "Body text"], ["--pms-text-muted", "Secondary text"], ["--pms-text-subtle", "Labels, captions"]] },
  { group: "Status", items: [["--pms-success", "Approved, finalized"], ["--pms-warning", "Waiting on someone"], ["--pms-danger", "Errors, destructive"], ["--pms-accent", "Informational"]] },
];

const TYPE_SCALE = [
  ["--text-3xl", "28px", "Stat numbers", { fontSize: "var(--text-3xl)", fontWeight: 700, letterSpacing: "-0.02em" }],
  ["--text-2xl", "24px", "Page title", { fontSize: "var(--text-2xl)", fontWeight: 600 }],
  ["--text-xl", "20px", "Section title", { fontSize: "var(--text-xl)", fontWeight: 600 }],
  ["--text-lg", "16px", "Card title", { fontSize: "var(--text-lg)", fontWeight: 600 }],
  ["--text-md", "14px", "Body (base size)", { fontSize: "var(--text-md)" }],
  ["--text-sm", "13px", "Meta text", { fontSize: "var(--text-sm)", color: "var(--pms-text-muted)" }],
  ["--text-xs", "12px", "Table headers, captions", { fontSize: "var(--text-xs)", color: "var(--pms-text-subtle)", fontWeight: 600 }],
];

const SPACING = [["--space-1", 4], ["--space-2", 8], ["--space-3", 12], ["--space-4", 16], ["--space-5", 20], ["--space-6", 24], ["--space-8", 32], ["--space-10", 40]];

const RADIUS = [["--radius-sm", 6], ["--radius-md", 8], ["--radius-lg", 12], ["--radius-xl", 16]];

const STATUSES = [
  "Not Started", "Draft", "Submitted", "Pending Approval", "Changes Requested", "Approved",
  "In Progress", "Completed", "Awaiting Assessments", "Pending HR Review", "Finalized",
];

const SAMPLE_ROWS = [
  { id: "E1004", name: "Priya Sharma", role: "Senior Software Engineer", score: 88.5, status: "Pending HR Review" },
  { id: "E1005", name: "Amit Patil", role: "Software Engineer", score: null, status: "Pending Approval" },
  { id: "E1003", name: "Rahul Deshmukh", role: "Engineering Manager", score: 91.2, status: "Finalized" },
];

function Swatch({ token, use }) {
  return (
    <li className="sg-swatch">
      <span className="sg-swatch-chip" style={{ background: `var(${token})` }} />
      <span className="sg-swatch-text">
        <code>{token}</code>
        <em>{use}</em>
      </span>
    </li>
  );
}

function Spec({ title, children, note }) {
  return (
    <Card title={title} subtitle={note}>
      {children}
    </Card>
  );
}

export default function StyleGuidePage() {
  const [tab, setTab] = useState("tokens");
  const [modal, setModal] = useState(false);
  const [value, setValue] = useState("");
  const [search, setSearch] = useState("");

  return (
    <div className="sg-page">
      <main className="sg-container page">
        <PageHeader
          title="PMS design system"
          subtitle="Tokens and components used across the Performance Management System. Rendered with the live components, so this page always matches the app."
          actions={<Button variant="secondary" onClick={() => window.print()}>Print</Button>}
        />

        <Tabs
          tabs={[
            { key: "tokens", label: "Foundations" },
            { key: "components", label: "Components" },
            { key: "patterns", label: "Patterns & rules" },
          ]}
          value={tab}
          onChange={setTab}
        />

        {tab === "tokens" && (
          <>
            <Spec title="Colour" note="Defined in src/styles/variables.css. Use the token, never a raw hex value.">
              <div className="sg-grid">
                {COLORS.map((group) => (
                  <div key={group.group}>
                    <h3 className="sg-subtitle">{group.group}</h3>
                    <ul className="sg-swatches">
                      {group.items.map(([token, use]) => <Swatch key={token} token={token} use={use} />)}
                    </ul>
                  </div>
                ))}
              </div>
            </Spec>

            <Spec title="Type scale" note="Body text is 14px. Sizes are in rem so browser zoom and OS font settings are respected.">
              <ul className="sg-type">
                {TYPE_SCALE.map(([token, px, use, style]) => (
                  <li key={token}>
                    <span style={style}>The quick brown fox</span>
                    <span className="sg-type-meta"><code>{token}</code> · {px} · {use}</span>
                  </li>
                ))}
              </ul>
            </Spec>

            <div className="grid-2">
              <Spec title="Spacing" note="4px step. Gaps between cards are 20px, padding inside a card is 20px.">
                <ul className="sg-bars">
                  {SPACING.map(([token, px]) => (
                    <li key={token}>
                      <span className="sg-bar" style={{ width: px }} />
                      <code>{token}</code>
                      <em>{px}px</em>
                    </li>
                  ))}
                </ul>
              </Spec>
              <Spec title="Radius & elevation" note="Cards use --radius-xl and --shadow-card. Popovers use --shadow-pop.">
                <ul className="sg-radius">
                  {RADIUS.map(([token, px]) => (
                    <li key={token}>
                      <span className="sg-radius-box" style={{ borderRadius: `var(${token})` }} />
                      <code>{token}</code>
                      <em>{px}px</em>
                    </li>
                  ))}
                </ul>
                <div className="sg-shadows">
                  <span className="sg-shadow" style={{ boxShadow: "var(--shadow-card)" }}>--shadow-card</span>
                  <span className="sg-shadow" style={{ boxShadow: "var(--shadow-raised)" }}>--shadow-raised</span>
                  <span className="sg-shadow" style={{ boxShadow: "var(--shadow-pop)" }}>--shadow-pop</span>
                </div>
              </Spec>
            </div>
          </>
        )}

        {tab === "components" && (
          <>
            <Spec title="Buttons" note="Primary for the one main action per screen. Secondary for everything else. Danger only for destructive actions.">
              <div className="sg-row">
                <Button icon={FiPlus}>Primary</Button>
                <Button variant="secondary" icon={FiDownload}>Secondary</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="danger" icon={FiTrash2}>Danger</Button>
                <Button loading>Saving</Button>
                <Button disabled>Disabled</Button>
                <Button size="sm">Small</Button>
              </div>
            </Spec>

            <Spec title="Status badges" note="One badge component drives every status in the app, so a status always looks the same wherever it appears.">
              <div className="sg-row">
                {STATUSES.map((s) => <StatusBadge key={s} status={s} />)}
              </div>
            </Spec>

            <Spec title="Stat tiles" note="Top of a dashboard. Clickable tiles take you to the list behind the number.">
              <div className="stat-grid">
                <StatTile icon={FiUsers} label="Direct reports" value={3} />
                <StatTile icon={FiTrendingUp} tone="info" label="Average score" value="88.5" hint="Rated employees only" />
                <StatTile icon={FiCheckCircle} tone="success" label="Finalized" value="1" />
              </div>
            </Spec>

            <Spec title="Form fields" note="Label, control, and one line of hint or error text. Errors are announced through aria-describedby.">
              <div className="grid-2">
                <FormField label="Goal title" required hint="Keep it under 120 characters.">
                  <input className="form-control" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Ship the attendance module" />
                </FormField>
                <FormField label="Weightage %" error="Weightage must total 100% across all goals.">
                  <input className="form-control" defaultValue="120" />
                </FormField>
              </div>
              <div className="sg-row">
                <SearchInput value={search} onChange={setSearch} placeholder="Search ID, name or email" label="Search employees" />
              </div>
            </Spec>

            <Spec title="Table" note="Sticky header, sideways scroll on small screens, pagination after 10 rows, and an empty state built in.">
              <DataTable
                label="Sample employees"
                rows={SAMPLE_ROWS}
                columns={[
                  { key: "name", label: "Employee", render: (r) => <PersonCell name={r.name} meta={r.role} /> },
                  { key: "score", label: "Final score", align: "right", render: (r) => (r.score === null ? "—" : r.score) },
                  { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
                ]}
              />
            </Spec>

            <div className="grid-2">
              <Spec title="Progress & meters">
                <ProgressBar value={85} label="Goal completion" showValue />
                <div style={{ height: 16 }} />
                <WeightageMeter goals={[{ id: "1", title: "Ship the module", weightage: 40 }, { id: "2", title: "Cut API time", weightage: 25 }, { id: "3", title: "Mentor engineers", weightage: 15 }]} />
                <div style={{ height: 16 }} />
                <RatingScale score={88.5} />
              </Spec>
              <Spec title="Feedback">
                <Banner tone="info" title="Goal setting is open">Submit before 30 September.</Banner>
                <div style={{ height: 12 }} />
                <Banner tone="warning" title="Changes requested">Your manager asked for an update on two goals.</Banner>
                <div style={{ height: 12 }} />
                <div className="sg-row">
                  <Button variant="secondary" onClick={() => setModal(true)}>Open a modal</Button>
                  <Avatar name="Priya Sharma" />
                  <Loader label="Loading" />
                </div>
              </Spec>
            </div>

            <Spec title="Empty state" note="Every list says what belongs there and what to do next, instead of showing a blank box.">
              <EmptyState title="No goals yet" text="Add your goals for this cycle and send them to your manager." action={<Button icon={FiPlus}>Add goal</Button>} />
            </Spec>

            <Modal open={modal} onClose={() => setModal(false)} title="Example modal" footer={<><Button variant="secondary" onClick={() => setModal(false)}>Cancel</Button><Button onClick={() => setModal(false)}>Confirm</Button></>}>
              <p>Focus is trapped here, Escape closes it, and focus returns to the button that opened it.</p>
            </Modal>
          </>
        )}

        {tab === "patterns" && (
          <>
            <Spec title="Layout & breakpoints">
              <ul className="sg-list">
                <li><strong>1080px and below:</strong> the view toggle and user name collapse to icons.</li>
                <li><strong>900px and below:</strong> the sidebar becomes a drawer behind the menu button, and page padding drops to 16px.</li>
                <li><strong>760px and below:</strong> tables keep their column widths and scroll sideways inside their card.</li>
                <li><strong>560px and below:</strong> the top bar tightens up; the cycle name keeps as much room as the bar can spare.</li>
              </ul>
            </Spec>

            <Spec title="Accessibility rules this app follows">
              <ul className="sg-list">
                <li>One visible focus ring everywhere, light on dark buttons, and a skip link to the main content.</li>
                <li>Text meets WCAG AA contrast, including muted labels and captions.</li>
                <li>Charts are hidden from screen readers and paired with the same numbers as text.</li>
                <li>Scrollable tables are focusable, so they can be scrolled with the keyboard.</li>
                <li>Headings run h1 to h2 in order; every page has one h1.</li>
                <li>Tap targets are at least 40px on touch screens.</li>
                <li>Animation is switched off for people who ask for reduced motion.</li>
              </ul>
            </Spec>

            <Spec title="Writing style">
              <ul className="sg-list">
                <li>Buttons say what happens: "Submit goal sheet", not "OK".</li>
                <li>Errors say what to fix: "Goal weightage must total exactly 100%. It is 80% now."</li>
                <li>Statuses are written in plain words, in title case, and always mean the same thing.</li>
                <li>Dates are shown as 30 Sept 2026, never as a raw timestamp.</li>
              </ul>
            </Spec>
          </>
        )}
      </main>
    </div>
  );
}
